import React, { useState, useEffect, useCallback } from 'react';
import { initLDProvider, createLDUser } from './context';
import { connect } from 'react-redux';
import initLDDebugTools from './debug';
import { extractUserFromJWT } from '../utils/jwt';
import { FeatureFlagProvider } from './FeatureFlagContext';

/**
 * LaunchDarkly Provider Component
 * This component initializes LaunchDarkly and provides the context to the app
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
const LaunchDarklyProvider = ({ children }) => {
  const [LDProvider, setLDProvider] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to create a user object for LaunchDarkly
  const getUserContext = useCallback(() => {
    // Check if user is authenticated
    const accessToken = localStorage.getItem('access');

    if (accessToken) {
      // Extract user info from JWT token
      const userInfo = extractUserFromJWT(accessToken);

      // Create a user object with user info from JWT
      return createLDUser({
        // Use the user_id from JWT as the LaunchDarkly user key
        userId: userInfo.userId,
        // No email or name as they're not in the JWT payload
        // No custom attributes as requested
      });
    } else {
      // Use anonymous user
      return createLDUser(null);
    }
  }, []);

  useEffect(() => {
    // Initialize LaunchDarkly when the component mounts
    const initializeLaunchDarkly = async () => {
      try {
        // Get user information
        const user = getUserContext();

        // Initialize the LaunchDarkly provider
        const Provider = await initLDProvider(user);
        setLDProvider(() => Provider);
      } catch (error) {
        console.error('Failed to initialize LaunchDarkly:', error);
        // Render the app without LaunchDarkly in case of error
        setLDProvider(() => ({ children }) => <>{children}</>);
      } finally {
        setLoading(false);
      }
    };

    initializeLaunchDarkly();
  }, [getUserContext]);

  // Expose the LaunchDarkly client to window for debugging
  useEffect(() => {
    if (LDProvider && typeof window !== 'undefined') {
      // Try to access the client through different possible properties
      const client =
        LDProvider.__ldClientInstance ||
        (LDProvider._context && LDProvider._context.client) ||
        (LDProvider.props && LDProvider.props.ldClient);

      if (client) {
        window.ldClient = client;

        // Initialize debugging tools
        initLDDebugTools();
      }
    }
  }, [LDProvider]);

  // Show loading state while LaunchDarkly is initializing
  if (loading || !LDProvider) {
    // You can replace this with a loading spinner or other UI
    return <div>Loading...</div>;
  }

  // Import the client initializer component
  const LDClientInitializer = React.lazy(() => import('./LDClientInitializer'));

  // Render the app with LaunchDarkly provider and client initializer
  // Wrap children with our FeatureFlagProvider to centralize flag access
  return (
    <LDProvider>
      <React.Suspense fallback={null}>
        <LDClientInitializer />
      </React.Suspense>
      <FeatureFlagProvider>{children}</FeatureFlagProvider>
    </LDProvider>
  );
};

// Connect to Redux store to get user context information
const mapStateToProps = state => ({
  locationSelectFormat: state.locationSelectFormat,
  dateFormat: state.dateFormat,
});

export default connect(mapStateToProps)(LaunchDarklyProvider);
